<template>
    <q-layout
            view="lhh LpR lff"
            container
            style="width: 100vw; height: 100vh"
    >
        <q-header
            class="bg-grey-9"
            :bordered="false"
        >
            <q-toolbar>
                <q-toolbar-title>
                    API documentation
                    <q-badge color="green" text-color="white" align="middle" :label="settings.version" />
                </q-toolbar-title>
                <q-btn flat @click="logout" v-if="isLoggedIn()">Logout</q-btn>
                <q-btn flat @click="drawer = !drawer" round dense icon="menu"/>
            </q-toolbar>
        </q-header>

        <q-drawer
                side="left"
                v-model="drawer"
                bordered
                :width="308"
                :dark="true"
                :breakpoint="500"
        >
            <q-scroll-area class="fit">
                <img src="@/assets/img/logo.png" class="logo" alt="logo">

                <div class="menu" v-if="isLoggedIn()">
                    <template v-for="(method, methodIndex) in settings.methods">
                        <div class="item"  @click="scrollToSection(methodIndex)">
                            <div v-if="method.title" class="title">{{method.title}}</div>
                            <div class="url">{{method.url}}
                                <div class="methods">
                                    <q-badge v-if="method.method.constructor === String"
                                             :color=methodsColorsMap[method.method]
                                             text-color="white"
                                             align="middle"
                                             :label="method.method"
                                    />

                                    <q-badge v-for="http in method.method" v-else
                                             :color=methodsColorsMap[http]
                                             text-color="white"
                                             align="middle"
                                             :label="http"
                                    />
                                </div>
                            </div>

                        </div>
                    </template>
                </div>


            </q-scroll-area>
        </q-drawer>

        <q-page-container>
            <q-page style="padding-top: 30px" class="q-pa-md" v-if="isLoggedIn()">
                <div class="mainDescription">
                    <div class="welcome">
                        Welcome to the API, created to automate the process of exchanging leads between systems. Our API provides a convenient and efficient programmatic interface for integration with your CRM, marketing platforms and other systems, ensuring instant data transfer.
                        The main goal of the API is to simplify and standardize the exchange of lead information between different participants in the process, including advertisers, partners and other stakeholders.
                    </div>

                    <div class="quote">
                        <div class="title">Key concepts:</div>
                        <ul>
                            <li><b>Lead:</b> Contact information of a potential client, including data such as name, phone, email, etc.</li>
                            <li><b>Validation:</b> Checking the correctness of the lead data before sending it to the system or client.</li>
                            <li><b>Access token (API Key):</b> A unique key that is used to authenticate requests to the API.</li>
                            <li><b>API Method:</b> An operation that can be performed through the interface (for example, creating a lead or sending a postback).</li>
                        </ul>

                        <div>
                            The API is designed using REST standards, which makes it easy to integrate with most modern web applications. Interaction with the interface is carried out through HTTP requests with support for JSON formats for ease of working with various types of data.
                        </div>
                    </div>
                    <div v-if="settings.methods">
                        <div class="h2">Methods</div>
                        <template v-for="(method, methodIndex) in settings.methods">
                            <div class="method">
                                <div class="http" :class="{active : method.isExpanded}" @click="toggleContent(methodIndex)" :id="'section-' + methodIndex">

                                    <div class="methods">
                                        <q-badge v-if="method.method.constructor === String"
                                                 :color=methodsColorsMap[method.method]
                                                 text-color="white"
                                                 align="middle"
                                                 :label="method.method"
                                        />

                                        <q-badge v-for="http in method.method" v-else
                                                 :color=methodsColorsMap[http]
                                                 text-color="white"
                                                 align="middle"
                                                 :label="http"
                                        />
                                    </div>

                                    <div class="url">{{settings.domain}}{{method.url}}</div>

                                    <q-icon name="add_circle_outline" class="arrow" />

                                </div>

                                <template v-if="method.isExpanded">
                                    <div v-if="method.title" class="h3 title">{{method.title}}</div>
                                    <div v-if="method.description" class="description">{{method.description}}</div>

                                    <div class="params" v-if="method.fields">
                                        <div>Request body:</div>
                                        <div class="type">{{method.type}}</div>
                                    </div>

                                    <div class="data">
                                        <div class="fields">

                                            <template v-for="(field, fieldKey) in method.fields">
                                                <div class="field">
                                                    <div class="name">
                                                        {{fieldKey}}
                                                        <span v-if="field.translated">{{field.translated}}</span>
                                                    </div>
                                                    <div class="params">
                                                        <ul>
                                                            <li v-if="checkPartnerField(fieldKey)" class="required">Required</li>
                                                            <li v-if="field.type">Type: <span>{{field.type}}</span></li>

                                                            <li v-if="field.description">
                                                              <div class="title">Description</div>
                                                              <div class="description" v-html="field.description"></div>
                                                            </li>

                                                            <li v-if="partner[fieldKey] && partner[fieldKey].mask">
                                                              <li>Mask: <span>{{partner[fieldKey].mask}}</span></li>
                                                            </li>

                                                            <li v-if="field.example">
                                                              <li>Example: <span>{{field.example}}</span></li>
                                                            </li>

                                                            <li v-if="field.containNumbers">May contain numbers: <span>{{field.containNumbers}}</span></li>
                                                            <li v-if="field.enum">
                                                                <div class="title">Options:</div>
                                                                <ul>
                                                                    <li v-for="(value, key) in field.enum">
                                                                        <span class="key" v-if="field.type !== 'string'">{{key}}</span>
                                                                        <span class="key" v-else>{{value}}</span> =>
                                                                        <span class="value">{{value}}</span>
                                                                    </li>
                                                                </ul>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </template>

                                            <template v-if="method.request">
                                              <div v-for="(request, requestKey) in method.request">
                                                <div v-if="request.title" class="codeTitle h3 title q-mb-lg" v-html="request.title"></div>
                                                <div class="dark-theme" v-if="request.request">
                                                  <div class="code" v-html="request.request"></div>
                                                </div>
                                              </div>
                                            </template>

                                        </div>
                                        <div class="response">
                                            <div class="authorization" v-if="method.auth">
                                                <div class="h3 title">
                                                    Authorization
                                                </div>

                                                <div class="headers">
                                                    <div class="subtitle">Headers</div>
                                                    <table class="headerParams">
                                                        <thead>
                                                        <tr>
                                                            <th>KEY</th>
                                                            <th>VALUE</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr>
                                                            <td>api-key</td>
                                                            <td>{{apiKey}}</td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                            </div>

                                            <div v-if="method.response" class="h3 title q-mb-lg">
                                              Example response
                                            </div>

                                            <div v-for="(response, responseKey) in method.response" v-if="method.response">
                                                <div class="response">
                                                    <div class="statusBar"
                                                         :class="{
                                                danger:  !response.isSuccess,
                                                success: response.isSuccess
                                            }"
                                                    >
                                                        <div class="code">
                                                            {{response.code}}
                                                        </div>
                                                        <div v-if="response.description" class="responseDescription">
                                                            {{response.description}}
                                                        </div>
                                                    </div>

                                                    <div class="dark-theme">
                                                        <vue-json-pretty :data="response.example" />
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </template>

                            </div>
                        </template>

                        <div class="h2">Contacts</div>
                        <div class="contacts nm">
                            If you have any technical questions or want to change the parameters being transmitted, please contact our manager in Telegram @vass_ka (<a href="https://t.me/vass_ka">https://t.me/vass_ka</a>)
                        </div>
                    </div>
                </div>
            </q-page>

            <q-page style="padding-top: 30px" class="q-pa-md" v-else>
                <q-form
                    @submit="auth"
                    class="q-gutter-md loginForm"
                >
                    <div class="title">Authorization</div>

                    <q-input
                        filled
                        v-model="apiKey"
                        label="API Key *"
                        lazy-rules
                        :rules="[ val => val && val.length > 10 || 'Invalid key']"
                    />

                    <div>
                        <q-btn label="Login" type="submit" color="primary"/>
                    </div>

                </q-form>
                <div class="contacts">
                    If you don't know your API key or want to get it, contact our manager in Telegram @k_vasiliieva (<a href="https://t.me/k_vasiliieva)">https://t.me/k_vasiliieva)</a>)
                </div>
            </q-page>
        </q-page-container>
    </q-layout>

</template>

<script setup>
import {onMounted, reactive, ref, watch} from "vue";
import {ajax} from "@/assets/js/functions";
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';

const isLoading = ref(true)
const drawer = ref(true)
const partnerFields = ref(true)
const settings = ref({})
const partner = ref(JSON.parse(localStorage.getItem('partner')) || {})
const apiKey = ref(localStorage.getItem('apiKey') || '')

const methodsColorsMap = ref({
  'GET' : 'positive',
  'POST' : 'primary',
  'PUT' : 'warning',
  'DELETE' : 'negative'
})

const formatJSON = (rawData) => {
  return JSON.stringify(rawData, null, 2)
}

const auth = () => {
  ajax({
    action: "documentationAuth",
    key: apiKey.value
  }, (data) => {
    isLoading.value = false
    partner.value = data
    localStorage.setItem('partner', JSON.stringify(data))
    getData()
  })
}
const isLoggedIn = () => {
  return partner.value.active !== undefined
}
const logout = () => {
  partner.value = {}
  localStorage.removeItem('apiKey')
  localStorage.removeItem('partner')
}

const checkPartnerField = (fieldName) => {
  if (!partner.value.required_fields) return false
  return partner.value.required_fields.includes(fieldName)
}
const toggleContent = (index) => {
  settings.value.methods[index].isExpanded = !settings.value.methods[index].isExpanded;
}

const scrollToSection = (index) => {
  const section = document.getElementById(`section-${index}`);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth' });
  }
};

onMounted(() => {
  auth()
  getData()
});

watch(apiKey, (newApiKey) => {
  localStorage.setItem('apiKey', newApiKey);
});

const getData = () => {
  drawer.value = !isMobile()
  ajax({
    action: "documentation",
    key: partner.value.api_key
  }, (data) => {
    console.log(data)
    isLoading.value = false
    settings.value = data
  })
}

const isMobile = () => {
  return !window.innerWidth >= 1000;
};
</script>


<style scoped>
.h2{
    font-size: 2rem;
    margin-top: 2rem;
}
.h3{
    font-size: 1.3rem;
    margin-top: 1.3rem;
}
.welcome{
    font-size: 1.1rem;
}
.logo{
    margin: 1rem auto;
    position: relative;
    display: block;
    user-select: none;
}
.quote{
    border: 1px solid #c1c1c1;
    border-radius: 0.5rem;
    padding: 1rem;
    margin: 1rem 0;
}
.quote .title{
    margin-bottom: 0.5rem;
    font-size: 1rem;
}
.dark-theme {
    background-color: #2e2e2e; /* Темный фон */
    padding: 10px;
    border-radius: 5px;
    color: #fff;
}

.dark-theme .vue-json-pretty {
    color: #e0e0e0; /* Светлый цвет текста */
}

.dark-theme .vue-json-pretty .json-key {
    color: #80cbc4; /* Цвет ключей JSON */
}

.dark-theme .vue-json-pretty .json-string {
    color: #ffab91; /* Цвет строковых значений JSON */
}

.dark-theme .vue-json-pretty .json-number {
    color: #ffd54f; /* Цвет числовых значений JSON */
}
.http {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2rem;
    padding: 1rem 2rem;
    margin: 1rem 0 2rem;
    border: 1px solid #a1a1a1;
    border-radius: 5px;
    cursor: pointer;
}
.http .url{
    font-size: 1.4rem;
}
.http .methods {
    display: flex;
    gap: 0.5rem;
}
.http .methods .q-badge{
    font-size: 1.2rem;
    line-height: 1.2rem;
}
.http .arrow{
    margin-left: auto;
    font-size: 2rem;
}
.http.active .arrow, .http:hover .arrow{
    color: #4CAF50;
}
.method > .params{
    display: flex;
    justify-content: space-between;
    font-size: 1.2rem;
    border-bottom: 1px dashed #a7a7a7;
    margin: 1rem 0;
}
.method > .params .type{
    background: #e7e7e7;
    padding: 0.3rem 1rem;
    border-radius: 5px;
    margin-bottom: 0.5rem;
}
.fields .field {
    display: flex;
    gap: 2rem;
}
.fields .field:not(:last-child){
    border-bottom: 1px solid #d2d2d2;
}
.fields .field ul{
    list-style: none;
    padding: 0;
}
.fields .field .name{
    font-size: 1rem;
    display: flex;
    flex-direction: column;
    min-width: 10rem;
}
.fields .field .name span{
    font-size: 0.8rem;
    font-weight: 300;
}
.fields .field .params span:not(.value){
    font-weight: 500;
}
.fields .field .params > ul > li{
    margin-bottom: 0.6rem;
}
.method .data {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}
.response {
    margin-bottom: 2rem;
}
.response .statusBar {
    display: flex;
    font-size: 1.2rem;
    justify-content: space-between;
    padding: 1rem 1rem;
}
.response .statusBar.danger{
    background: #f4433629;
    color: red;
}
.response .statusBar.success{
    background: #8bc34a36;
    color: #4CAF50;
}
.required{
    color: red;
}
.menu {
    display: flex;
    flex-direction: column;
    align-items: baseline;
    margin: 2rem;
    font-size: 1.2rem;
}
.menu .item{
    display: block;
    margin-bottom: 1rem;
    width: 100%;
}
.menu .item:hover{
    opacity: 0.8;
    cursor: pointer;
}
.menu .item .url{
    font-size: 0.8rem;
    display: flex;
    justify-content: space-between;
}
.loginForm {
    width: 700px;
    max-width: 100%;
    border: 1px solid #d1d1d1;
    padding: 1rem;
    margin: 2rem;
    border-radius: 0.4rem;
}
.loginForm .title{
    font-size: 1.3rem;
    margin-bottom: 2rem;
}
.contacts {
    width: 700px;
    max-width: 100%;
    padding: 1rem;
    margin: 2rem;
    background: #e5e5e5;
    border-radius: 0.4rem;
}
.contacts.nm{
    margin: 0;
}
.authorization {
    margin-bottom: 2rem;
    background: #ebebeb;
    padding: 0.2rem 1rem 1rem;
    border-radius: 0.4rem;
}
.headerParams{
    width: 100%;
    text-align: left;
    margin-top: 1rem;
}
.code{
  white-space: pre-wrap;
}
@media all and (max-width: 768px){
    .method .data {
        display: flex;
        flex-direction: column;
    }
}

</style>